import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent } from "../../misc/Dialog";
import { Button } from "../../misc/Button";
import { FaTimes } from "react-icons/fa";
import { Post as PostType } from "../../../types/posts";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import { Popover, PopoverContent, PopoverTrigger } from "../../misc/Popover";
import { postTime } from "../../campaign/types";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../../misc/Card";
import { Textarea } from "../../misc/Textarea";
import { Separator } from "../../misc/Seperator";
import { cn } from "../../../utils/classMerger";
import { CalendarIcon, Clock, Save } from "lucide-react";
import InlineWarning from "../../misc/InlineWarning";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../misc/Select";
import { format, isBefore, setHours, setMinutes } from "date-fns";
import CustomDatePicker from "../../misc/CutomDatePicker";
import { EMOJI_CATEGORIES } from "../../misc/AllEmojis";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useInstagramHashtagValidation } from "../../../hooks/useInstagramHashtagValidation";
import ReconfirmOverlay from "../../misc/ReconfirmOverlay";
import PostGraphicPicker from "../../misc/custom-posts/PostGraphicPicker";
import ConfirmPopup from "../../misc/ConfirmPopup";


interface EditPostDialogProps {
    isOpen: boolean;
    onClose: () => void;
    post: PostType;
    onSave: (updatedPost: Partial<PostType>) => void;
    setIsOpen: (open: boolean) => void;
    shouldSyncParentState?: boolean;
    bgColor?: {
        type: "solid" | "gradient";
        color?: string;
        from?: string;
        to?: string;
        direction?: string;
    };
    textColor?: string;
    fontSize?: number;
    fontFamily?: string;
    setBgColor?: React.Dispatch<
        React.SetStateAction<{
            type: "solid" | "gradient";
            color?: string;
            from?: string;
            to?: string;
            direction?: string;
        }>
    >;
    setTextColor?: React.Dispatch<React.SetStateAction<string>>;
    setFontSize?: React.Dispatch<React.SetStateAction<number>>;
    setFontFamily?: React.Dispatch<React.SetStateAction<string>>;
    hideFooter?: boolean;
}

const EditPostDialog: React.FC<EditPostDialogProps> = ({
    isOpen,
    onClose,
    post,
    onSave,
    setIsOpen,
    shouldSyncParentState,
    bgColor,
    textColor,
    fontSize,
    fontFamily,
    setBgColor,
    setTextColor,
    setFontSize,
    setFontFamily,
    hideFooter = false,
}) => {
    const originalOpenHouse = Array.isArray(post.content)
        ? post.content[0]
        : null;

    const [content, setContent] = useState<string>(
        Array.isArray(post.content) ? post.content[1] : post.content
    );
    const [openHouseContent, setOpenHouseContent] = useState<string | null>(
        Array.isArray(post.content) ? post.content[0] : null
    );
    const [footer, setFooter] = useState<string>(post.footer || "");
    const [title, setTitle] = useState<string>(post?.title || "");

    useEffect(() => {
        if (!isOpen) return;
        if (Array.isArray(post.content)) {
            setOpenHouseContent(post.content[0]);
            setContent(post.content[1]);
        } else {
            setContent(post.content);
            setOpenHouseContent(null);
        }
        setFooter(post.footer || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post.content, post.footer, isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        setTitle(post?.title || "");
    }, [post.title, isOpen]);

    // For Twitter, no character limit is enforced; for others we use a limit.
    const [charLimit, setCharLimit] = useState<number>(1000);

    const [footerLimit, setFooterLimit] = useState<number>(1000);

    const [date, setDate] = useState<Date>(
        post.scheduledFor ? new Date(post.scheduledFor) : new Date()
    );
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });

    const [isReconfirmOpen, setIsReconfirmOpen] = useState(false);

    const [pendingChanges, setPendingChanges] = useState<Partial<PostType>>({});

    // We'll store time in 24-hour format internally.
    const initialHour = post.scheduledFor
        ? new Date(post.scheduledFor).getHours()
        : 0;
    const initialMinute = post.scheduledFor
        ? new Date(post.scheduledFor).getMinutes()
        : 0;
    const [time, setTime] = useState<postTime>({
        hour: initialHour,
        minute: initialMinute,
    });

    const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const emojiBtnRef = useRef<HTMLButtonElement>(null);
    const isPasteRef = useRef(false);
    const isPasteTitleRef = useRef(false);
    const isPasteFooterRef = useRef(false);
    const [wasPastedAndTruncated, setWasPastedAndTruncated] = useState(false);
    const [wasTitlePasteTruncated, setWasTitlePasteTruncated] = useState(false);
    const [wasFooterPasteTruncated, setWasFooterPasteTruncated] = useState(false);
    const [showTruncationConfirm, setShowTruncationConfirm] = useState(false);

    
    // Reset date and time when post changes (for platform switching)
    useEffect(() => {
        if (post.scheduledFor) {
            const postDate = new Date(post.scheduledFor);
            setDate(postDate);
            setTime({
                hour: postDate.getHours(),
                minute: postDate.getMinutes(),
            });
        }
    }, [post.scheduledFor, post.platform]);

    useEffect(() => {
        // Set character limit based on platform.
        // Manual flow (hideFooter=true) uses the actual platform limits.
        // AI-enhanced flow uses the more conservative limits the AI targets.
        if (post.type === "Lead Generation") {
            setCharLimit(200);
            setFooterLimit(300);
            return;
        }

        const platform = post.platform.toLowerCase();
        if (hideFooter) {
            // Manual flow — actual platform limits
            if (platform === "instagram") {
                setCharLimit(2200);
            } else if (platform === "facebook") {
                setCharLimit(50000);
            } else if (platform === "linkedin") {
                setCharLimit(3000);
            } else if (platform === "twitter") {
                setCharLimit(3000);
            } else if (platform === "pinterest") {
                setCharLimit(500);
            } else if (platform === "google" || platform === "gmb") {
                setCharLimit(1500);
            } else if (platform === "youtube") {
                setCharLimit(5000);
            } else {
                setCharLimit(1500);
            }
            setFooterLimit(platform === "twitter" ? 280 : 300);
        } else {
            // AI-enhanced flow — conservative limits used by the AI
            if (platform === "instagram") {
                setCharLimit(1600);
                setFooterLimit(300);
            } else if (platform === "facebook") {
                setCharLimit(50000);
                setFooterLimit(300);
            } else if (platform === "linkedin") {
                setCharLimit(2100);
                setFooterLimit(300);
            } else if (platform === "twitter") {
                setCharLimit(3700);
                setFooterLimit(280);
            } else if (platform === "pinterest") {
                setCharLimit(200);
                setFooterLimit(300);
            } else if (platform === "google" || platform === "gmb") {
                setCharLimit(1000);
                setFooterLimit(300);
            } else if (platform === "youtube") {
                setCharLimit(4700);
                setFooterLimit(300);
            } else {
                setCharLimit(1000);
                setFooterLimit(300);
            }
        }
    }, [post.platform, post.type, hideFooter]);

    const {
        isViolated: isInstagramHashtagViolated,
        errorMessage: hashtagErrorMessage,
    } = useInstagramHashtagValidation(content, post.platform);

    // Compute period from time (24-hour format)
    const period = time.hour >= 12 ? "PM" : "AM";

    // Convert 24-hour hour to 12-hour format for the UI
    const displayHour = (() => {
        const h = time.hour % 12;
        return h === 0 ? 12 : h;
    })();

    // Hour and minute options (full range since we won't restrict them now)
    const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
    const minuteOptions = Array.from({ length: 60 }, (_, i) => i);

    const performSave = () => {
        if (isInstagramHashtagViolated) {
            toast.error(
                <CustomToast
                    message={hashtagErrorMessage}
                    type="error"
                />,
                { autoClose: 4000 }
            );
            return;
        }

        // Validate YouTube hashtag limit
        if (post.platform?.toLowerCase() === "youtube") {
            const hashtagCount = content.split(/\s+/).filter(t => t.startsWith("#") && t.length > 1).length;
            if (hashtagCount > 15) {
                toast.error(
                    <CustomToast message="YouTube allows a maximum of 15 hashtags in the description." type="error" />,
                    { autoClose: 4000 }
                );
                return;
            }
        }

        // Validate content length
        if (content.trim().length === 0) {
            toast.error(
                <CustomToast message="Content cannot be empty." type="error" />,
                { autoClose: 3000 }
            );
            return;
        }

        // Adjust the hour based on AM/PM
        let adjustedHour = displayHour;
        if (period === "PM" && displayHour !== 12) {
            adjustedHour += 12;
        } else if (period === "AM" && displayHour === 12) {
            adjustedHour = 0;
        }

        const scheduledDateTime = setHours(
            setMinutes(new Date(date), time.minute),
            adjustedHour
        );

         // Check if the selected time is in the past
        const now = new Date();
        if (isBefore(scheduledDateTime, now)) {
            toast.error(
                <CustomToast
                    message="Cannot schedule in the past."
                    type="error"
                />,
                { autoClose: 3000 }
            );
            return;
        }
        
        if(shouldSyncParentState){
        setIsOpen(false);
        onSave({
            content: content,
            title: title,
            footer: footer,
            scheduledFor: scheduledDateTime.toISOString(),
        });
      }else{

        // Determine what changed
        const changes: Partial<PostType> = {};

        const newScheduledForDate = scheduledDateTime.toISOString();

        const originalContent = Array.isArray(post.content) ? post.content[1] : post.content;
        const contentChanged = content !== originalContent;
        const footerChanged = footer !== post.footer;
        const titleChanged = title !== post.title;
        const timeChanged = newScheduledForDate !== post.scheduledFor;

        // Save content as-is (no hashtag appending) — preview combines them at display time
        if (contentChanged) changes.content = content;
        if (timeChanged) changes.scheduledFor = newScheduledForDate;
        // Only include footer for non-Lead Generation posts
        // Only include footer and graphics for Lead Generation posts if applicable
        if (footerChanged && post.type !== "Lead Generation") changes.footer = footer;
        if (post.type === "Lead Generation") {
            changes.background = bgColor;
            changes.textColor = textColor;
            changes.fontSize = fontSize;
            changes.fontFamily = fontFamily;
        }
        if (titleChanged) changes.title = title;

        if (openHouseContent !== originalOpenHouse) {
            console.log("Open house content changed");
            console.log("originalOpenHouse", originalOpenHouse);
            console.log("openHouseContent", openHouseContent);
            // hold onto what we'll send, but wait for user confirmation
            setPendingChanges(changes);
            setIsReconfirmOpen(true);
            return;
        }
        // If nothing changed, you could choose to do nothing or still call onSave:
        if (Object.keys(changes).length === 0) {
            setIsOpen(false);
            toast.success(
                <CustomToast
                    message="Post updated successfully."
                    type="success"
                />,
                { autoClose: 3000 }
            );
            return;
        }

        onSave({ ...changes });
      }
    };

    const handleSave = () => {
        if (wasPastedAndTruncated || wasTitlePasteTruncated || wasFooterPasteTruncated) {
            setShowTruncationConfirm(true);
            return;
        }
        performSave();
    };

   const handleProceed = () => {
        let mergedContent = content;
        if (openHouseContent) {
            const withDoubleNewline = `${openHouseContent}\n\n${content}`;
            if (withDoubleNewline.length > charLimit) {
                // Trim only the \n\n separator to fit within the limit
                const withSingleNewline = `${openHouseContent}\n${content}`;
                if (withSingleNewline.length > charLimit) {
                    mergedContent = `${openHouseContent}${content}`;
                } else {
                    mergedContent = withSingleNewline;
                }
            } else {
                mergedContent = withDoubleNewline;
            }
        }
        const newChanges: Partial<PostType> = {
            ...pendingChanges,
            content: mergedContent,
        };
        setIsReconfirmOpen(false);
        setPendingChanges(newChanges);
        console.log("newChanges:", newChanges);
        onSave({ ...newChanges, manual: true });
    };

    const handleResetOpenHouse = () => {
        // restore original, close overlay
        setOpenHouseContent(originalOpenHouse);
        setIsReconfirmOpen(false);
    };

    const handleHourChange = (value: string) => {
        // value is in 12-hour format. We'll store in state as 24-hour format
        const selectedHour12 = parseInt(value, 10);
        let newHour = selectedHour12;
        if (period === "PM" && selectedHour12 !== 12) {
            newHour = selectedHour12 + 12;
        } else if (period === "AM" && selectedHour12 === 12) {
            newHour = 0;
        }
        setTime((prev) => ({ ...prev, hour: newHour }));
    };

    const handleMinuteChange = (value: string) => {
        const newMinute = parseInt(value, 10);
        setTime((prev) => ({ ...prev, minute: newMinute }));
    };

    // Updated handlePeriodChange function
    const handlePeriodChange = (value: string) => {
        const currentDisplayHour = time.hour % 12 === 0 ? 12 : time.hour % 12;
        const newHour =
            value === "AM"
                ? currentDisplayHour === 12
                    ? 0
                    : currentDisplayHour
                : currentDisplayHour === 12
                ? 12
                : currentDisplayHour + 12;
        setTime((prev) => ({ ...prev, hour: newHour }));
    };

    // Update emoji insertion based on footerType
    const handleInsertEmoji = (emoji: string) => {
        if (!textareaRef.current) return;
        const start = textareaRef.current.selectionStart;
        const end = textareaRef.current.selectionEnd;
        const newContentLength = content.length - (end - start) + emoji.length;
        const exceedsLimit = newContentLength > charLimit;
        if (exceedsLimit) {
            toast.error(
                <CustomToast
                    message="Character limit reached, cannot insert emoji."
                    type="error"
                />,
                { autoClose: 3000 }
            );
            return;
        }
        const newContent =
            content.substring(0, start) + emoji + content.substring(end);
        setContent(newContent);
        setTimeout(() => {
            if (!textareaRef.current) return;
            textareaRef.current.focus();
            const newPosition = start + emoji.length;
            textareaRef.current.setSelectionRange(newPosition, newPosition);
        }, 0);
    };

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target as Node)
            ) {
                setIsPopoverOpen(false);
            }
        }

        if (isPopoverOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isPopoverOpen]);

    const isMobile = useIsMobile(1100);
    return (
        <>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent
                    className={cn(
                        "relative w-full h-full m-0 flex flex-col items-center justify-center bg-white p-0 mt-0",
                        post.type === "Lead Generation"
                            ? "max-w-5xl"
                            : "max-w-2xl"
                    )}
                    dark={true}
                >
                    <div
                        className={`${
                            isMobile ? "py-0" : "w-full"
                        } absolute top-0`}
                    >
                        <button
                            className="absolute top-4 right-4 text-secondary hover:text-primary"
                            onClick={onClose}
                            aria-label="Close Dialog"
                        >
                            <FaTimes className="h-6 w-6" />
                        </button>

                        <Card className={`w-full mx-auto p-0 mt-0`}>
                            <CardHeader>
                                <CardTitle>Edit Post</CardTitle>
                                <CardDescription className="text-center">
                                    Edit the content and schedule of your social
                                    media post here.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4 pt-0">
                                <div className="space-y-2">
                                    {(post.platform?.toLowerCase() === "youtube" || (post.title && post.title.length > 0)) ? (
                                        <>
                                            <label className="text-sm font-medium">
                                                Post Title
                                            </label>
                                            <Textarea
                                                ref={textareaRef}
                                                value={title}
                                                rows={2}
                                                onPaste={() => { isPasteTitleRef.current = true; }}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    if (isPasteTitleRef.current) {
                                                        isPasteTitleRef.current = false;
                                                        setWasTitlePasteTruncated(raw.length > 100);
                                                    } else {
                                                        setWasTitlePasteTruncated(false);
                                                    }
                                                    setTitle(raw.substring(0, 100));
                                                }}
                                                placeholder="Write your post title here..."
                                                className={cn(
                                                    "min-h-[60px] resize-none",
                                                    title.length >= 100 && "border-red-500 focus:border-red-500 focus-visible:ring-red-500"
                                                )}
                                            />
                                            <div
                                                className="flex justify-end"
                                                id="title-char-count"
                                            >
                                                <span className={
                                                    title.length >= 100
                                                        ? "text-red-500"
                                                        : title.length >= 90
                                                        ? "text-amber-500"
                                                        : ""
                                                }>
                                                    {title.length} / 100
                                                    characters
                                                </span>
                                            </div>
                                        </>
                                    ) : null}

                                    {openHouseContent !== null ? (
                                        <div className="mb-4">
                                            <label className="text-sm font-medium">
                                                Open House Content
                                            </label>
                                            <Textarea
                                                ref={textareaRef}
                                                value={openHouseContent}
                                                onChange={(e) => {
                                                    const remaining = Math.max(0, charLimit - content.length);
                                                    setOpenHouseContent(e.target.value.substring(0, remaining));
                                                }}
                                                placeholder="Write your open house content here..."
                                                className={cn(
                                                    "min-h-[150px] resize-none",
                                                    (content.length + openHouseContent.length) >= charLimit
                                                        ? "border-red-500 focus:border-red-500 focus-visible:ring-red-500"
                                                        : ""
                                                )}
                                            />
                                        </div>
                                    ) : null}

                                    <label className="text-sm font-medium">
                                        Post Content
                                    </label>
                                    {(() => {
                                        const ohLen = openHouseContent?.length ?? 0;
                                        const combinedLength = content.length + ohLen;
                                        const postContentLimit = Math.max(0, charLimit - ohLen);
                                        return (
                                        <>
                                    <Textarea
                                        ref={textareaRef}
                                        value={content}
                                        onPaste={() => { isPasteRef.current = true; }}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            const truncated = raw.substring(0, postContentLimit);
                                            if (isPasteRef.current) {
                                                isPasteRef.current = false;
                                                setWasPastedAndTruncated(raw.length > postContentLimit);
                                            } else {
                                                setWasPastedAndTruncated(false);
                                            }
                                            setContent(truncated);
                                        }}
                                        placeholder="Write your post content here..."
                                        className={cn(
                                            post.type === "Lead Generation" ? "min-h-[150px]" : "min-h-[350px]",
                                            "resize-none rounded-lg",
                                            combinedLength >= charLimit
                                                ? "border-red-500 focus:border-red-500 focus-visible:ring-red-500"
                                                : "",
                                            isInstagramHashtagViolated && "border-red-400 focus-visible:ring-red-400"
                                        )}
                                    />

                                    <div className="flex justify-end">
                                        <span className={
                                            combinedLength >= charLimit
                                                ? "text-red-500"
                                                : combinedLength >= charLimit * 0.9
                                                ? "text-amber-500"
                                                : ""
                                        }>
                                            {combinedLength} / {charLimit} characters
                                        </span>
                                    </div>
                                        </>
                                        );
                                    })()}
                                    {isInstagramHashtagViolated && (
                                        <InlineWarning
                                            message={hashtagErrorMessage}
                                        />
                                    )}
                                    {post.platform?.toLowerCase() === "youtube" &&
                                        content.split(/\s+/).filter(t => t.startsWith("#") && t.length > 1).length > 15 && (
                                        <InlineWarning
                                            message={`YouTube allows a maximum of 15 hashtags in the description. You currently have ${content.split(/\s+/).filter(t => t.startsWith("#") && t.length > 1).length}.`}
                                        />
                                    )}
                                    {!hideFooter && post.includeFooter && post.type !== "Lead Generation" ? (
                                        <div>
                                            <label className="text-sm font-medium">
                                                Post Footer
                                            </label>
                                            <Textarea
                                                value={footer}
                                                onPaste={() => { isPasteFooterRef.current = true; }}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    if (isPasteFooterRef.current) {
                                                        isPasteFooterRef.current = false;
                                                        setWasFooterPasteTruncated(raw.length > footerLimit);
                                                    } else {
                                                        setWasFooterPasteTruncated(false);
                                                    }
                                                    setFooter(raw.substring(0, footerLimit));
                                                }}
                                                placeholder="Write your post footer here..."
                                                className={cn(
                                                    "min-h-[200px] resize-none mt-2",
                                                    footer.length >= footerLimit && "border-red-500 focus:border-red-500 focus-visible:ring-red-500"
                                                )}
                                            />

                                            <div className="flex justify-end mt-2">
                                                <span className={
                                                    footer.length >= footerLimit
                                                        ? "text-red-500"
                                                        : footer.length >= footerLimit * 0.9
                                                        ? "text-amber-500"
                                                        : ""
                                                }>
                                                    {footer.length} /{" "}
                                                    {footerLimit} characters
                                                </span>
                                            </div>
                                        </div>
                                    ) : null}
                                    <div className="flex justify-between items-center text-sm text-muted-foreground">
                                        {!hideFooter && !isMobile && (
                                            <div className="flex justify-between items-center">
                                                <Button
                                                    id="insert-emoji-btn"
                                                    ref={emojiBtnRef}
                                                    onClick={() => {
                                                        if (!isPopoverOpen && emojiBtnRef.current) {
                                                            const rect = emojiBtnRef.current.getBoundingClientRect();
                                                            const popoverHeight = 340; // max-h-[300px] + close button row
                                                            const spaceBelow = window.innerHeight - rect.bottom;
                                                            const top = spaceBelow >= popoverHeight
                                                                ? rect.bottom + 4
                                                                : rect.top - popoverHeight - 4;
                                                            setPopoverPos({ top, left: rect.left });
                                                        }
                                                        setIsPopoverOpen(!isPopoverOpen);
                                                    }}
                                                >
                                                    😊 Insert Emoji
                                                </Button>
                                            </div>
                                        )}

                                        {/* Custom Popover */}
                                        {!hideFooter && isPopoverOpen && (
                                            <div
                                                className="fixed bg-white border shadow-lg rounded p-2 w-80 z-50"
                                                style={{ top: popoverPos.top, left: popoverPos.left }}
                                                ref={popoverRef}
                                            >
                                                <div className="flex justify-end">
                                                    <button
                                                        onClick={() =>
                                                            setIsPopoverOpen(
                                                                false
                                                            )
                                                        }
                                                        className="p-1 hover:bg-gray-100 rounded"
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                </div>
                                                <div className="max-h-[300px] overflow-auto p-2">
                                                    {Object.entries(
                                                        EMOJI_CATEGORIES
                                                    ).map(
                                                        ([
                                                            category,
                                                            emojis,
                                                        ]) => (
                                                            <div
                                                                key={category}
                                                                className="mb-4"
                                                            >
                                                                <h3 className="mb-2 text-sm font-medium text-primary">
                                                                    {category
                                                                        .replace(
                                                                            /-/g,
                                                                            " "
                                                                        )
                                                                        .replace(
                                                                            /\b\w/g,
                                                                            (
                                                                                c
                                                                            ) =>
                                                                                c.toUpperCase()
                                                                        )}
                                                                </h3>
                                                                <div className="grid grid-cols-6 gap-2">
                                                                    {emojis.map(
                                                                        (
                                                                            emoji
                                                                        ) => (
                                                                            <button
                                                                                key={
                                                                                    emoji
                                                                                }
                                                                                onClick={() =>
                                                                                    handleInsertEmoji(
                                                                                        emoji
                                                                                    )
                                                                                }
                                                                                className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 rounded"
                                                                            >
                                                                                {
                                                                                    emoji
                                                                                }
                                                                            </button>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {(post.type === "Lead Generation" || 
                                    (post.type === "preview" && post.background)) &&
                                    bgColor &&
                                    setBgColor &&
                                    textColor &&
                                    setTextColor &&
                                    (
                                        <>
                                            <Separator className="my-4" />
                                            <div className="space-y-4">
                                                <label className="text-sm font-medium">
                                                    Post Graphics
                                                </label>
                                                <div className="border rounded-lg p-4 bg-gray-50/50">
                                                    <PostGraphicPicker
                                                        isOpen={isOpen}
                                                        onClose={onClose}
                                                        isMobile={isMobile}
                                                        generalInstructions={content}
                                                        bgColor={bgColor}
                                                        textColor={textColor}
                                                        fontSize={fontSize || 24}
                                                        fontFamily={fontFamily || "Roboto"}
                                                        setBgColor={setBgColor}
                                                        setTextColor={setTextColor}
                                                        setFontSize={setFontSize || (() => {})}
                                                        setFontFamily={setFontFamily || (() => {})}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                <Separator className="my-4" />
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">
                                        Post Time
                                    </label>
                                    <div
                                        className={`flex items-center space-x-2 ${
                                            isMobile
                                                ? "flex-col space-y-2"
                                                : "flex-row"
                                        }`}
                                    >
                                        {isMobile ? (
                                            <div className="w-full">
                                                {isCalendarOpen && (
                                                    <div className="w-full mb-2">
                                                        <CustomDatePicker
                                                            date={date}
                                                            setDate={(d) => { setDate(d); setIsCalendarOpen(false); }}
                                                            restriction="month"
                                                        />
                                                    </div>
                                                )}
                                                <Button
                                                    id="open-calendar-btn"
                                                    variant="outline"
                                                    className={cn(
                                                        "w-full justify-start text-left font-normal",
                                                        !date && "text-muted-foreground"
                                                    )}
                                                    onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                                                >
                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                    {date ? format(date, "PPP") : <span>Change date</span>}
                                                </Button>
                                            </div>
                                        ) : (
                                            <Popover
                                                open={isCalendarOpen}
                                                onOpenChange={setIsCalendarOpen}
                                            >
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        id="open-calendar-btn"
                                                        variant="outline"
                                                        className={cn(
                                                            "w-[240px] justify-start text-left font-normal",
                                                            !date && "text-muted-foreground"
                                                        )}
                                                    >
                                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                                        {date ? format(date, "PPP") : <span>Change date</span>}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent
                                                    className="w-auto p-0"
                                                    align="start"
                                                    portalled={false}
                                                >
                                                    <CustomDatePicker
                                                        date={date}
                                                        setDate={setDate}
                                                        restriction="month"
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        )}
                                        <div className="flex items-center space-x-2">
                                            <Clock className="h-4 w-4 text-muted-foreground" />
                                            <Select
                                                value={displayHour.toString()}
                                                onValueChange={handleHourChange}
                                            >
                                                <SelectTrigger
                                                    className="w-[70px]"
                                                    id="hour-select"
                                                >
                                                    <SelectValue placeholder="Hour" />
                                                </SelectTrigger>
                                                <SelectContent className="cursor-pointer">
                                                    {hourOptions.map((hour) => (
                                                        <SelectItem
                                                            key={hour}
                                                            value={hour.toString()}
                                                        >
                                                            {hour
                                                                .toString()
                                                                .padStart(
                                                                    2,
                                                                    "0"
                                                                )}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <Select
                                                value={time.minute.toString()}
                                                onValueChange={
                                                    handleMinuteChange
                                                }
                                            >
                                                <SelectTrigger
                                                    className="w-[70px]"
                                                    id="minute-select"
                                                >
                                                    <SelectValue placeholder="Min" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {minuteOptions.map(
                                                        (minute) => (
                                                            <SelectItem
                                                                key={minute}
                                                                value={minute.toString()}
                                                            >
                                                                {minute
                                                                    .toString()
                                                                    .padStart(
                                                                        2,
                                                                        "0"
                                                                    )}
                                                            </SelectItem>
                                                        )
                                                    )}
                                                </SelectContent>
                                            </Select>
                                            <Select
                                                value={period}
                                                onValueChange={
                                                    handlePeriodChange
                                                }
                                            >
                                                <SelectTrigger
                                                    className="w-[70px]"
                                                    id="am-pm-select"
                                                >
                                                    <SelectValue placeholder="AM/PM" />
                                                </SelectTrigger>
                                                <SelectContent id="am-pm-select">
                                                    <SelectItem value="AM">
                                                        AM
                                                    </SelectItem>
                                                    <SelectItem value="PM">
                                                        PM
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                            <CardFooter className={post.type === "Lead Generation" ? "justify-start" : ""}>
                                <Button
                                    id="save-changes-btn"
                                    className={post.type === "Lead Generation" ? "" : "w-full"}
                                    onClick={handleSave}
                                    disabled={isInstagramHashtagViolated || content.length > charLimit}
                                >
                                    <Save className="mr-2 h-4 w-4" />
                                    Save Changes
                                </Button>
                            </CardFooter>
                        </Card>
                    </div>
                    <ConfirmPopup
                        open={showTruncationConfirm}
                        title="Content Was Truncated"
                        message={`You pasted content into ${[
                            wasTitlePasteTruncated && "Post Title",
                            wasPastedAndTruncated && "Post Content",
                            wasFooterPasteTruncated && "Post Footer",
                        ].filter(Boolean).join(", ")} that exceeded the character limit and it was truncated.`}
                        messageTwo="Would you like to go back and review it, or continue with the truncated content?"
                        confirmText="Continue"
                        cancelText="Back"
                        confirmVariant="destructive"
                        onConfirm={() => {
                            setShowTruncationConfirm(false);
                            setWasPastedAndTruncated(false);
                            setWasTitlePasteTruncated(false);
                            setWasFooterPasteTruncated(false);
                            performSave();
                        }}
                        onCancel={() => setShowTruncationConfirm(false)}
                    />
                    <ReconfirmOverlay
                        isOpen={isReconfirmOpen}
                        onClose={() => {
                            setIsReconfirmOpen(false);
                        }}
                        cancelClose={() => {
                            handleResetOpenHouse();
                            setIsReconfirmOpen(false);
                        }}
                        onConfirm={handleProceed}
                        title="You’ve edited Open House details"
                        confirmText="Proceed"
                    >
                        <div className="text-left" id="manual-mode-desc">
                            <p className="mt-1 text-gray-600 text-sm sm:text-base">
                                This post is currently managed by Marabot and
                                automatically shows open-house details based on
                                MLS.
                            </p>
                            <p className="mt-1 text-gray-600 text-sm sm:text-base">
                                If you edit the open house details, the post
                                will be switched to <strong>Manual Mode</strong>{" "}
                                and Marabot will stop making automatic updates.
                            </p>
                            <p className="mt-1 text-gray-600 text-sm sm:text-base">
                                By proceeding, please note that the following
                                will apply for the current post:
                            </p>
                            <ul className="list-disc list-inside mt-2 text-gray-600 text-sm sm:text-base">
                                <li>
                                    Canceled open house details will{" "}
                                    <strong>not</strong> be removed from this
                                    post.
                                </li>
                                <li>
                                    Newly added open houses will{" "}
                                    <strong>not</strong> be included in this
                                    post.
                                </li>
                                <li>
                                    You’ll be responsible for keeping this post
                                    up to date.
                                </li>
                            </ul>
                        </div>
                    </ReconfirmOverlay>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default EditPostDialog;
